import {OAuthPageContent} from '@/src/features/auth/core/oauth-types'
import {useStore} from '@/src/shared/hooks/useStore'
import {getClient} from '@/src/shared/utils/api-factory'
import {Routes} from '@/src/config/routes'
import {toast} from 'react-toastify'
import {useRouter} from 'nextjs-toploader/app'
import {ApiError} from '@/src/ports/services/api'
import {OAuthProvider, settings} from '@/src/config/settings'
import {getInjection} from '@runlab/di'
import {useEffect, useState} from 'react'
import {OAuthEvents, OAuthEventTypes} from '@/src/core/domain/events/oauth.events'
import type {OpenSecureWindowOptions} from '@capgo/capacitor-inappbrowser'

import {isIOS, isMobile} from '@/src/shared/utils/functions'

interface UseOAuthProps {
  provider: ValuesOf<typeof OAuthProvider>
}

export const useOAuth = ({provider}: UseOAuthProps) => {
  const router = useRouter()
  const rootStore = useStore<OAuthPageContent>()
  const [disabled, setDisabled] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getInjection('IEventBus').subscribe(OAuthEventTypes.SignInProcess, () => {
      setDisabled(true)
      setLoading(true)
    })
    getInjection('IEventBus').subscribe(OAuthEventTypes.SignInDone, () => {
      setDisabled(false)
      setLoading(false)
    })
  }, [])

  if (!rootStore.pageData?.content.oauth_providers.includes(provider)) {
    throw new Error(`Провайдер ${provider} недоступен`)
  }

  const signIn = async () => {
    getInjection('IInstrumentationService').startSpanAsync(
      {
        name: `Начало процесса OAuth с провайдером ${provider}`,
        root: true,
        attributes: {
          provider,
        },
      },
      async () => {
        try {
          await getInjection('IEventBus').publish(OAuthEvents.OAuthSignInProcessStartedEvent())
          const client = getClient('api')
          const data = await client.post<{redirect: string}>(
            Routes.api.auth.thirdParty.start(provider),
          )
          if (isMobile()) {
            if (isIOS()) {
              const {InAppBrowser} = await import('@capgo/capacitor-inappbrowser')
              const {redirectedUri} = await InAppBrowser.openSecureWindow({
                authEndpoint: data.redirect,
                redirectUri: `capacitor://${settings.APP_NAME}/oauth/yandex/callback/`,
                prefersEphemeralWebBrowserSession: false,
              } as OpenSecureWindowOptions)

              const redirectUrl = redirectedUri.replace(`capacitor://${settings.APP_NAME}`, '')

              router.push(
                redirectUrl.replace(
                  /\/oauth\/(.*)\/callback\/\?(.*)/,
                  '/oauth/callback/?provider=$1&$2',
                ),
              )
            } else {
              router.push(data.redirect)
            }
          } else {
            router.push(data.redirect)
          }
        } catch (e: unknown) {
          console.error(e)
          if (e instanceof ApiError) {
            toast.error(e.messageNormalized)
          } else {
            toast.error('Ошибка')
          }
        } finally {
          await getInjection('IEventBus').publish(OAuthEvents.OAuthSignInProcessDoneEvent())
        }
      },
    )
  }

  return {
    signIn,
    disabled,
    loading,
  }
}

export const useOAuthProviders = (): OAuthPageContent['oauth_providers'] => {
  const rootStore = useStore<OAuthPageContent>()

  return rootStore.pageData?.content.oauth_providers ?? []
}
